/**
 * 鹿児島県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 * 12品すべてが農水省「うちの郷土料理」の鹿児島県30品から採ってある。
 *
 * Lv12「豚骨」について。
 *  これは骨付きあばら肉を芋焼酎で炒りつけ、味噌と黒砂糖で煮込んだ**煮物**で、
 *  福岡の豚骨ラーメンとはまったく別物。福岡県版が公開済みなので、
 *  並べて見たときに誤解されないよう、短縮名を「豚骨煮」にしてある。
 *  正式な呼称は農水省の見出しどおり「豚骨」。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 白熊の発祥店・創業年、かるかんの考案者、つけあげの由来、あくまきの由来、
 *    げたんはの名の由来は、いずれも農水省が伝聞形か複数説の併記。断定しない。
 *  - さつまいも伝来の年（1698年／1705年）も「とされています」。書かない。
 *  - 「日本三大○○」は鹿児島のどの料理・景色についても国の資料に見当たらない。使わない。
 *  - 「つけあげ」が県内の呼称。「さつまあげ」で書かない。
 *  - 「鶏飯」は「けいはん」と読む。「とりめし」ではない。
 *  - 黒糖の「黒糖地獄」と鶏飯の由来は、どちらも支配の歴史。事実として静かに書き、
 *    驚き型の豆知識にしない。
 *  - 鶏刺し（生の鶏肉）は食品衛生上の注意が要るので採用しない。
 *
 * 本番画像は public/assets/foods/kagoshima/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：黒糖・たんかん・げたんは・あくまき（店で食べられる品に差し替え）
 * - 両棒餅（ぢゃんぼ餅）：南北朝時代に懐良親王に谷山城主が出した、という逸話は農水省ページにあるが、人物にかかわる起源話なので書かない
 * - 両棒餅（ぢゃんぼ餅）：名の由来のうち「両」を中国語の読み（リャン）がなまったという説は、農水省ページで並記された説のひとつ。県観光連盟の説（大小の刀）だけを書いた。説が割れていることは notWritten に残す
 * - 首折れサバの刺身：サバの生食の安全については、公式ページの「鮮度の落ちが速く、一般的に生食はNGのイメージ」の一文にとどまるので、安全をうたう言い方はしない
 * - 黒豚とんかつ：「西郷隆盛もこよなく愛した」「徳川斉昭が珍味と言った」は出典に「といわれています」とある人物の逸話なので書かない
 * - 黒豚とんかつ：「有名店では行列ができる」は店の宣伝になりうるので書かない
 * - 黒豚しゃぶしゃぶ：出汁の種類（昆布など）は出典にないので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { KAGOSHIMA_FOOD_HULLS } from './kagoshimaFoodShapes.generated';
import { kagoshimaSceneryTour } from './kagoshimaScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'jambo-mochi',
    level: 1,
    name: '両棒餅（ぢゃんぼ餅）',
    shortName: 'ぢゃんぼ餅',
    image: '/assets/foods/kagoshima/01_jambo_mochi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'もち米粉の生地を平たい円形にしてゆで、水にとって冷ましてから素焼きにし、1個に2本の竹串を刺す。砂糖じょうゆのたれや味噌だれをからめて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/jambomochi_kagoshima.html',
    icon: { label: 'ぢゃんぼ餅', shape: 'mochi', baseColor: '#f2ead8', accentColor: '#a5651f' },
    reward: {
      unlockScore: 200,
      area: '鹿児島市',
      headline: '2本の竹串に刺した餅',
      tip: '「ぢゃんぼ（両棒）」は鹿児島弁で、武士が携えていた大小の刀を指す言葉。2本の竹串を使うのは、その武士の姿になぞらえたもの。',
      links: searchLinks('鹿児島市 磯', 'ぢゃんぼ餅'),
      official: [{ label: '鹿児島県観光連盟「地元で愛されるローカルおやつ」', url: 'https://www.kagoshima-kankou.com/gourmet/sweets/top' }],
    },
  },
  {
    id: 'karukan',
    level: 2,
    name: 'かるかん',
    shortName: 'かるかん',
    image: '/assets/foods/kagoshima/02_karukan.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '山芋を、蒸し上げる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/karukan_kagoshima.html',
    icon: { label: 'かるかん', shape: 'mochi', baseColor: '#f4f0e6', accentColor: '#d8c8a8' },
    reward: {
      unlockScore: 500,
      area: '鹿児島県内全域',
      headline: '山芋とかるかん粉を蒸した白い菓子。今はあん入りが一般的。',
      tip: 'すりおろした山芋に水・砂糖・かるかん粉を混ぜ、蒸し器で25分から30分。',
      links: searchLinks('鹿児島県', 'かるかん'),
    },
  },
  {
    id: 'tsukeage',
    level: 3,
    name: 'つけあげ',
    shortName: 'つけあげ',
    image: '/assets/foods/kagoshima/03_tsukeage.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '砂糖を入れるから、甘い。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tsukeage_kagoshima.html',
    icon: { label: 'つけあげ', shape: 'dumpling', baseColor: '#d8a850', accentColor: '#8a5a2a' },
    reward: {
      unlockScore: 1000,
      area: '鹿児島県内全域',
      headline: '魚のすり身を揚げたもの。砂糖を入れた甘口が鹿児島の味。他県ではさつまあげとも呼ぶ。',
      tip: 'すり身に豆腐や地酒を混ぜ、160度ほどの油で黄金色になるまで揚げる。',
      links: searchLinks('鹿児島県', 'つけあげ'),
    },
  },
  {
    id: 'kibinago',
    level: 4,
    name: 'きびなごの刺身',
    shortName: 'きびなご',
    image: '/assets/foods/kagoshima/04_kibinago.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '包丁を使わず、手で開く。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/kibinagonosashimi_kagoshima.html',
    icon: { label: 'きびなご', shape: 'fish', baseColor: '#c8d8e0', accentColor: '#4a7a9a' },
    reward: {
      unlockScore: 1800,
      area: '薩摩川内市（甑島）',
      headline: '体長およそ10センチの魚を手開きにし、菊の花のように並べる「菊花造り」。',
      tip: '酢味噌が一般的。しょうが醤油やみょうがも合う。',
      links: searchLinks('薩摩川内市', 'きびなご'),
    },
  },
  {
    id: 'abura-somen',
    level: 5,
    name: '油ゾーメン',
    shortName: '油ゾーメン',
    image: '/assets/foods/kagoshima/05_abura_somen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '炒めるときに、だしを入れる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/aburazomen_kagoshima.html',
    icon: { label: '油ゾーメン', shape: 'ramen', baseColor: '#e8dcc0', accentColor: '#6a8a3a' },
    reward: {
      unlockScore: 3000,
      area: '奄美地域',
      headline: 'そうめんを豚肉といりこで炒め、だし汁を回しかける。',
      tip: '炒めるときにだし汁を入れるのが奄美流。油とだしが乳化して麺に絡む。最後にニラを入れる。',
      links: searchLinks('奄美', '油ゾーメン'),
    },
  },
  {
    id: 'shirokuma',
    level: 6,
    name: '白熊',
    shortName: '白熊',
    image: '/assets/foods/kagoshima/06_shirokuma.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '色あざやかに、のせる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/shirokuma_kagoshima.html',
    icon: { label: '白熊', shape: 'mochi', baseColor: '#f4f4f0', accentColor: '#e05a7a' },
    reward: {
      unlockScore: 4500,
      area: '鹿児島県内全域',
      headline: '削った氷に加糖練乳をかけ、果物と小豆を彩りよくのせた氷菓。',
      tip: '練乳の甘さが氷で薄まり、果物がアクセントになる。色あざやかにのせるのがポイント。',
      links: searchLinks('鹿児島市', '白熊'),
    },
  },
  {
    id: 'satsumajiru',
    level: 7,
    name: 'さつま汁',
    shortName: 'さつま汁',
    image: '/assets/foods/kagoshima/07_satsumajiru.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '鶏の出汁が、味噌に溶ける。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/satsumajiru_kagoshima.html',
    icon: { label: 'さつま汁', shape: 'pot', baseColor: '#c8a058', accentColor: '#8a6a3a' },
    reward: {
      unlockScore: 6500,
      area: '鹿児島県内全域',
      headline: '鶏肉と根菜を煮込んだ味噌仕立ての汁。',
      tip: '具材を炒めてから出汁を加え、野菜が煮えたら味噌で味付け。具と味つけは家庭と季節でちがう。',
      links: searchLinks('鹿児島県', 'さつま汁'),
    },
  },
  {
    id: 'keihan',
    level: 8,
    name: '鶏飯',
    shortName: '鶏飯',
    image: '/assets/foods/kagoshima/08_keihan.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '「けいはん」と読む。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/keihan_kagoshima.html',
    icon: { label: '鶏飯', shape: 'roe', baseColor: '#f0e8d8', accentColor: '#d8b040' },
    reward: {
      unlockScore: 9000,
      area: '奄美地域',
      headline: 'ごはんに具をのせ、鶏ガラのスープをかけて食べる奄美の料理。',
      tip: 'ごはんは少なめ、スープ多めにして、さらさらとかきこむのが良しとされる。',
      links: searchLinks('奄美', '鶏飯'),
    },
  },
  {
    id: 'kubiore-saba',
    level: 9,
    name: '首折れサバの刺身',
    shortName: '首折れサバ',
    image: '/assets/foods/kagoshima/09_kubiore_saba.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '屋久島沖で一本釣りしたゴマサバを、釣り上げた直後に首を折り、すぐに血抜きして氷水に入れ、とれたての鮮度を保つ。臭みがほとんどなく、身の締まった刺身で味わえる。',
    source:
      'https://www.kagoshima-kankou.com/gourmet/fish/saba',
    icon: { label: '首折れサバ', shape: 'sashimi-platter', baseColor: '#c8505a', accentColor: '#7a9ab8' },
    reward: {
      unlockScore: 12000,
      area: '屋久島町',
      headline: '釣ってすぐ首を折るサバ',
      tip: '鹿児島でとれるサバはゴマサバが主で、マサバより脂肪分が少ない。屋久島では薄切りの身を使う「サバしゃぶ」や、すき焼き風の「サバスキ」を出す店もある。',
      links: searchLinks('屋久島町', '首折れサバ'),
      official: [{ label: '屋久島観光協会「屋久島の特産品・お土産」', url: 'https://yakukan.jp/specialty.html' }],
    },
  },
  {
    id: 'kurobuta-tonkatsu',
    level: 10,
    name: '黒豚とんかつ',
    shortName: '黒豚とんかつ',
    image: '/assets/foods/kagoshima/10_kurobuta_tonkatsu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '甘しょ（さつまいも）入りのえさで育てたかごしま黒豚を、サクサクの衣で包んで揚げる。黒豚は脂っぽさがなくさっぱりとした食感で、歯切れがよく柔らかいのが特徴とされる。',
    source:
      'https://www.kagoshima-kankou.com/gourmet/meat/pork',
    icon: { label: '黒豚とんかつ', shape: 'feast', baseColor: '#d9a441', accentColor: '#f2e6d8' },
    reward: {
      unlockScore: 16000,
      area: '鹿児島市',
      headline: 'さつまいもで育てた黒豚を揚げる',
      tip: '約400年前、島津家が琉球から移入したのがかごしま黒豚のルーツといわれる。昭和40年代には絶滅の危機に直面したが、生産者の努力で復活した。',
      links: searchLinks('鹿児島市', '黒豚とんかつ'),
      official: [{ label: '鹿児島県観光連盟「かごしまブランド「黒豚・黒牛・地鶏」を召し上がれ」', url: 'https://www.kagoshima-kankou.com/gourmet/meat/top' }],
    },
  },
  {
    id: 'tonkotsu-ni',
    level: 11,
    name: '豚骨（骨付き豚の煮込み）',
    shortName: '豚骨煮',
    image: '/assets/foods/kagoshima/11_tonkotsu_ni.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '骨付きあばら肉を、黒砂糖と味噌で。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/tonkotsu_kagoshima.html',
    icon: { label: '豚骨煮', shape: 'hotpot', baseColor: '#7a4a28', accentColor: '#d8b878' },
    reward: {
      unlockScore: 22000,
      area: '鹿児島県内全域',
      headline: '骨付きのあばら肉を芋焼酎で炒りつけ、味噌と黒砂糖で煮込む。ラーメンとは別の料理。',
      tip: '表面に焦げ目をつけてから芋焼酎を振り、強火でアルコールを飛ばす。大根やこんにゃくと煮込む。',
      links: searchLinks('鹿児島県', '豚骨 郷土料理'),
    },
  },
  {
    id: 'kurobuta-shabushabu',
    level: 12,
    name: '黒豚しゃぶしゃぶ',
    shortName: '黒豚しゃぶしゃぶ',
    image: '/assets/foods/kagoshima/12_kurobuta_shabushabu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '薄く切ったかごしま黒豚を出汁にくぐらせて食べる。調理法が簡単なぶん、黒豚本来のうま味がそのまま際立つ。県の黒豚生産者協議会の会員は、甘しょ入りのえさを決まりに沿って与えている。',
    source:
      'https://www.kagoshima-kankou.com/gourmet/meat/pork',
    icon: { label: 'しゃぶしゃぶ', shape: 'hotpot', baseColor: '#f2e6e0', accentColor: '#5f9e3a' },
    reward: {
      unlockScore: 30000,
      area: '鹿児島市',
      headline: '薄切りの黒豚を出汁にくぐらせる',
      tip: '鹿児島市の天文館は南九州最大の繁華街で、食の集積地。あちこちの飲食店で、黒豚や黒牛といった特産品が楽しめる。',
      links: searchLinks('鹿児島市 天文館', '黒豚しゃぶしゃぶ'),
      official: [{ label: '鹿児島県観光連盟「かごしまブランド「黒豚・黒牛・地鶏」を召し上がれ」', url: 'https://www.kagoshima-kankou.com/gourmet/meat/top' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const kagoshimaFoodStage: StageDefinition = {
  regionId: 'kagoshima',
  regionName: '鹿児島県',
  mode: 'food',
  title: '鹿児島めしマージ',
  theme: {
    primaryColor: '#8a3a2a',
    secondaryColor: '#d9a441',
    accentColor: '#e8c060',
    backgroundColor: '#181312',
  },
  /** 背景ツアー「鹿児島めぐり」。軸は時間帯。屋久島の朝から奄美の夜へ。 */
  scenery: {
    tour: kagoshimaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, KAGOSHIMA_FOOD_HULLS),
};
